/**
 * API contracts for BabyBillion Admin Console.
 * Use these endpoints and shapes when implementing the backend.
 * Base URL is from config (apiUrl from '../config/api').
 *
 * Auth: All endpoints except POST /login require:
 *   Header: Authorization: Bearer <token>
 *   (Token is returned from login and stored in the client.)
 *
 * Standard error response (4xx/5xx):
 *   Body: { message?: string, error?: string, code?: string, details?: unknown }
 *   Status: 400 validation, 401 unauthorized, 403 forbidden, 404 not found, 5xx server
 */

// ---------------------------------------------------------------------------
// COMMON TYPES (for reference; not exported as runtime types)
// ---------------------------------------------------------------------------
// Success wrapper: { data: T, message?: string }
// List success:   { data: T[], message?: string }
// Pagination (optional): query params ?page=1&limit=50; response may include total/count if supported

// ---------------------------------------------------------------------------
// AUTH
// ---------------------------------------------------------------------------

/** POST /login - Authenticate admin user; returns success (client stores token via login callback) */
export const AUTH_LOGIN = {
    method: 'POST',
    path: '/login',
    /**
     * Request: application/json
     * Body: {
     *   username: string (required),
     *   password: string (required)
     * }
     *
     * Response: 200
     * Body: {
     *   data?: { token: string, user?: { id: string, username: string } },
     *   token?: string,  // alternative at top level
     *   message?: string
     * }
     * Client expects a token in response (data.token or token) to store and send as Bearer.
     *
     * Errors: 401 invalid credentials, 400 validation
     * Body: { message?: string, error?: string }
     */
};

// ---------------------------------------------------------------------------
// CATEGORY
// ---------------------------------------------------------------------------

/** GET /categories - List all categories */
export const CATEGORY_LIST = {
    method: 'GET',
    path: '/cms/admin/categories',
    /**
     * Request: (optional) query params – page, limit (for server-side pagination if supported)
     *
     * Response: 200
     * Body: {
     *   data: Array<{
     *     id: string,
     *     name: string,
     *     status: 'Active' | 'Inactive',
     *     thumbnailUrl: string
     *   }>,
     *   message?: string
     * }
     */
};

/** POST /categories - Create a category (multipart/form-data) */
export const CATEGORY_CREATE = {
    method: 'POST',
    path: 'cms/admin/create-categories',
    /**
     * Request: multipart/form-data
     *   - name: string (required)
     *   - status: 'Active' | 'Inactive' (required)
     *   - age_groups: repeated form field (list of '0-3' | '3-6' | '6+')
     *   - thumbnail: File (image, required)
     */
};

/** GET /categories/:id - Get single category (for edit/duplicate) */
export const CATEGORY_GET = {
    method: 'GET',
    path: 'cms/admin/get-category/:id',
    pathFor: (id) => `cms/admin/get-category/${id}`,
};

/** PUT /categories/:id - Update a category (multipart/form-data) */
export const CATEGORY_UPDATE = {
    method: 'PUT',
    path: 'cms/admin/update-category/:id',
    pathFor: (id) => `cms/admin/update-category/${id}`,
    /**
     * Request: multipart/form-data
     *   - name: string (required)
     *   - status: 'Active' | 'Inactive' (required)
     *   - age_groups: repeated form field (list of '0-3' | '3-6' | '6+')
     *   - thumbnail: File (optional; omit to keep existing)
     */
};

/** DELETE /categories/:id - Delete (remove) a category */
export const CATEGORY_DELETE = {
    method: 'DELETE',
    path: 'cms/admin/delete-category/:id',
    pathFor: (id) => `cms/admin/delete-category/${id}`,

};

// ---------------------------------------------------------------------------
// CMS BULK UPLOAD (CSV → job; check status; job history)
// ---------------------------------------------------------------------------

/** POST /cms/admin/bulk-upload - Upload CSV file; returns job_id */
export const BULK_UPLOAD_CSV = {
    method: 'POST',
    path: '/cms/admin/bulk-upload',

};

/** GET /cms/admin/bulk-upload/jobs/:jobId - Get job status and progress */
export const BULK_UPLOAD_JOB_STATUS = {
    method: 'GET',
    path: '/cms/admin/bulk-upload/jobs/:jobId',
    pathFor: (jobId) => `/cms/admin/bulk-upload/jobs/${encodeURIComponent(jobId)}`,

};

/** GET /cms/admin/bulk-upload/jobs - List recent jobs (e.g. last 10) */
export const BULK_UPLOAD_JOB_LIST = {
    method: 'GET',
    path: '/cms/admin/bulk-upload/jobs',

};

// ---------------------------------------------------------------------------
// TAG (hashtag-style; name only)
// ---------------------------------------------------------------------------

/** GET /tags - List all tags */
export const TAG_LIST = {
    method: 'GET',
    path: '/tags',
    /**
     * Response: 200
     * Body: {
     *   data: Array<{ id: string, name: string }>,
     *   message?: string
     * }
     */
};

/** POST /tags - Create a tag (JSON) */
export const TAG_CREATE = {
    method: 'POST',
    path: '/tags',
    /**
     * Request: application/json
     * Body: { name: string } (required; stored without leading #, e.g. "kids")
     *
     * Response: 201
     * Body: {
     *   data: { id: string, name: string },
     *   message?: string
     * }
     * Errors: 400 validation (e.g. duplicate name), 401 unauthorized, 5xx server
     */
};

/** DELETE /tags/:id - Delete a tag */
export const TAG_DELETE = {
    method: 'DELETE',
    path: '/tags/:id',
    pathFor: (id) => `/tags/${id}`,
    /**
     * Response: 200 or 204
     * Body: { message?: string } or empty
     * Errors: 404 not found, 401 unauthorized
     */
};

// ---------------------------------------------------------------------------
// PLAYLIST
// ---------------------------------------------------------------------------

/** GET /playlists - List all playlists */
export const PLAYLIST_LIST = {
    method: 'GET',
    path: '/cms/admin/playlists',
    /**
     * Request: (optional) query params – page, limit
     *
     * Response: 200
     * Body: {
     *   data: Array<{
     *     id: string,
     *     title: string,
     *     status: 'ACTIVE' | 'INACTIVE',
     *     popularity_score: number,
     *     rank_score: number,
     *     small_thumbnail: string,
     *     thumbnail_url: string,
     *     thumbnail_url_landscape: string
     *   }>,
     *   message?: string
     * }
     */
};

/** POST /playlists - Create a playlist (multipart/form-data) */
export const PLAYLIST_CREATE = {
    method: 'POST',
    path: 'cms/admin/create-playlist',
    /**
     * Request: multipart/form-data
     *   - title: string (required)
     *   - status: 'ACTIVE' | 'INACTIVE' (required)
     *   - popularity_score: string (number as string, required)
     *   - rank_score: string (number as string, required)
     *   - age_groups: repeated form field (list of '0-3' | '3-6' | '6+')
     *   - small_thumbnail: File (image, required)
     *   - thumbnail_url: File (image, required)
     *   - thumbnail_url_landscape: File (image, required)
     *
     * Response: 201
     * Body: {
     *   data: {
     *     id: string,
     *     title: string,
     *     status: string,
     *     popularity_score: number,
     *     rank_score: number,
     *     small_thumbnail: string,
     *     thumbnail_url: string,
     *     thumbnail_url_landscape: string
     *   },
     *   message?: string
     * }
     * Errors: 400 validation, 401 unauthorized, 5xx server
     */
};

/** GET /playlists/:id - Get single playlist (for edit/duplicate) */
export const PLAYLIST_GET = {
    method: 'GET',
    path: 'cms/admin/get-playlist/:id',
    pathFor: (id) => `cms/admin/get-playlist/${id}`,
    /**
     * Response: 200
     * Body: {
     *   data: {
     *     id: string,
     *     title: string,
     *     status: 'ACTIVE' | 'INACTIVE',
     *     popularity_score: number,
     *     rank_score: number,
     *     small_thumbnail: string,
     *     thumbnail_url: string,
     *     thumbnail_url_landscape: string
     *   },
     *   message?: string
     * }
     * Errors: 404 not found, 401 unauthorized
     */
};

/** PUT /playlists/:id - Update a playlist (multipart/form-data) */
export const PLAYLIST_UPDATE = {
    method: 'PUT',
    path: 'cms/admin/update-playlist/:id',
    pathFor: (id) => `cms/admin/update-playlist/${id}`,
    /**
     * Request: multipart/form-data
     *   - title: string (required)
     *   - status: 'ACTIVE' | 'INACTIVE' (required)
     *   - popularity_score: string (required)
     *   - rank_score: string (required)
     *   - age_groups: repeated form field (list of '0-3' | '3-6' | '6+')
     *   - small_thumbnail: File (optional; omit to keep existing)
     *   - thumbnail_url: File (optional)
     *   - thumbnail_url_landscape: File (optional)
     *
     * Response: 200
     * Body: {
     *   data: { id, title, status, popularity_score, rank_score, small_thumbnail, thumbnail_url, thumbnail_url_landscape },
     *   message?: string
     * }
     * Errors: 400 validation, 404 not found, 401 unauthorized, 5xx server
     */
};

/** DELETE /playlists/:id - Delete a playlist */
export const PLAYLIST_DELETE = {
    method: 'DELETE',
    path: 'cms/admin/delete-playlist/:id',
    pathFor: (id) => `cms/admin/delete-playlist/${id}`,
    /**
     * Response: 200 or 204
     * Body: { message?: string } or empty
     * Errors: 404 not found, 401 unauthorized
     */
};

// ---------------------------------------------------------------------------
// VIDEO
// ---------------------------------------------------------------------------

/** GET /videos - List all videos */
export const VIDEO_LIST = {
    method: 'GET',
    path: '/cms/admin/videos',
    /**
     * Request: (optional) query params – page, limit
     *
     * Response: 200
     * Body: {
     *   data: Array<{
     *     id: string,
     *     title: string,
     *     channel_name: string,
     *     feed_key: string,
     *     status: string,
     *     thumbnail_url: string,
     *     video_url: string,
     *     age_buckets: string[],
     *     content_types: string[],
     *     playlist_ids?: string[],
     *     category_ids?: string[]
     *   }>,
     *   message?: string
     * }
     */
};

/** POST /videos/upload - Upload video file (multipart); returns URL for video_url */
export const VIDEO_UPLOAD = {
    method: 'POST',
    path: '/videos/upload',
    /**
     * Request: multipart/form-data
     *   - video: File (video file, required)
     *
     * Response: 200 or 201
     * Body: {
     *   data: { video_url: string },
     *   message?: string
     * }
     * Errors: 400 validation, 401 unauthorized, 5xx server
     */
};

/** POST /videos/thumbnail/generate - Generate thumbnail for a video by video ID */
export const VIDEO_THUMBNAIL_GENERATE = {
    method: 'POST',
    path: '/cms/admin/thumbnail/generate',
    /**
     * Request: application/json
     * Body: { video_id: string } (required)
     *
     * Response: 200
     * Body: {
     *   data: { thumbnail_url: string },
     *   message?: string
     * }
     * Errors: 400 validation, 404 video not found, 401 unauthorized, 5xx server
     */
};

/** POST /videos/thumbnail/upload - Upload thumbnail image (multipart); returns URL for thumbnail_url */
export const VIDEO_THUMBNAIL_UPLOAD = {
    method: 'POST',
    path: '/videos/thumbnail/upload',
    /**
     * Request: multipart/form-data
     *   - thumbnail: File (image, required)
     *
     * Response: 200 or 201
     * Body: {
     *   data: { thumbnail_url: string },
     *   message?: string
     * }
     * Errors: 400 validation, 401 unauthorized, 5xx server
     */
};

/** POST /videos - Create a video (JSON). Use VIDEO_UPLOAD and/or VIDEO_THUMBNAIL_UPLOAD first to get URLs if needed. */
export const VIDEO_CREATE = {
    method: 'POST',
    path: '/videos',
    /**
     * Request: application/json
     * Body: {
     *   title: string (required),
     *   channel_name?: string,
     *   feed_key?: string,
     *   status?: string (e.g. 'active', 'inactive'; default 'active'),
     *   thumbnail_url: string (required),
     *   video_url: string (required),
     *   age_buckets?: string[] (e.g. ['0-3', '3-6', '6+']),
     *   content_types?: string[] (e.g. ['Licensed', 'Original']),
     *   playlist_ids?: string[],
     *   category_ids?: string[]
     * }
     *
     * Response: 201
     * Body: {
     *   data: {
     *     id: string,
     *     title: string,
     *     channel_name: string,
     *     feed_key: string,
     *     status: string,
     *     thumbnail_url: string,
     *     video_url: string,
     *     age_buckets: string[],
     *     content_types: string[],
     *     playlist_ids: string[],
     *     category_ids: string[]
     *   },
     *   message?: string
     * }
     * Errors: 400 validation, 401 unauthorized, 5xx server
     */
};

/** GET /videos/:id - Get single video (for edit/duplicate) */
export const VIDEO_GET = {
    method: 'GET',
    path: 'cms/admin/videos/:id',
    pathFor: (id) => `cms/admin/videos/${id}`,
    /**
     * Response: 200
     * Body: {
     *   data: {
     *     id: string,
     *     title: string,
     *     channel_name: string,
     *     feed_key: string,
     *     status: string,
     *     thumbnail_url: string,
     *     video_url: string,
     *     age_buckets: string[],
     *     content_types: string[],
     *     playlist_ids?: string[],
     *     category_ids?: string[]
     *   },
     *   message?: string
     * }
     * Errors: 404 not found, 401 unauthorized
     */
};

/** PUT /videos/:id - Update a video (JSON). Partial update supported; only send fields to change. */
export const VIDEO_UPDATE = {
    method: 'PUT',
    path: '/videos/:id',
    pathFor: (id) => `/videos/${id}`,
    /**
     * Request: application/json
     * Body: same shape as VIDEO_CREATE; all fields optional (partial update).
     * Example: { thumbnail_url: string } to only update thumbnail.
     *
     * Response: 200
     * Body: {
     *   data: { id, title, channel_name, feed_key, status, thumbnail_url, video_url, age_buckets, content_types, playlist_ids, category_ids },
     *   message?: string
     * }
     * Errors: 400 validation, 404 not found, 401 unauthorized, 5xx server
     */
};

/** DELETE /videos/:id - Delete a video */
export const VIDEO_DELETE = {
    method: 'DELETE',
    path: '/videos/:id',
    pathFor: (id) => `/videos/${id}`,
    /**
     * Response: 200 or 204
     * Body: { message?: string } or empty
     * Errors: 404 not found, 401 unauthorized
     */
};

// ---------------------------------------------------------------------------
// USER
// ---------------------------------------------------------------------------

/** GET /users - List all users */
export const USER_LIST = {
    method: 'GET',
    path: '/users',
    /**
     * Request: (optional) query params – page, limit
     *
     * Response: 200
     * Body: {
     *   data: Array<{
     *     user_id: string,
     *     age_group: string,
     *     email: string | null,
     *     google_id: string | null,
     *     is_paid: boolean,
     *     name: string,
     *     phone_number: string,
     *     status: string
     *   }>,
     *   message?: string
     * }
     */
};

/** GET /users/:id - Get single user by user_id (for edit). Id must be URL-encoded if it contains special chars. */
export const USER_GET = {
    method: 'GET',
    path: '/users/:id',
    pathFor: (id) => `/users/${encodeURIComponent(id)}`,
    /**
     * Response: 200
     * Body: {
     *   data: {
     *     user_id: string,
     *     age_group: string,
     *     email: string | null,
     *     google_id: string | null,
     *     is_paid: boolean,
     *     name: string,
     *     phone_number: string,
     *     status: string
     *   },
     *   message?: string
     * }
     * Errors: 404 not found, 401 unauthorized
     */
};

/** PUT /users/:id - Update a user (JSON). phone_number is not editable from this API. */
export const USER_UPDATE = {
    method: 'PUT',
    path: '/users/:id',
    pathFor: (id) => `/users/${encodeURIComponent(id)}`,
    /**
     * Request: application/json
     * Body: {
     *   name?: string,
     *   age_group?: string,
     *   email?: string | null,
     *   google_id?: string | null,
     *   is_paid?: boolean,
     *   status?: string
     * }
     * Do not send phone_number.
     *
     * Response: 200
     * Body: {
     *   data: { user_id, age_group, email, google_id, is_paid, name, phone_number, status },
     *   message?: string
     * }
     * Errors: 400 validation, 404 not found, 401 unauthorized, 5xx server
     */
};

/** DELETE /users/:id - Delete/remove a user. Allowed only when user status is inactive. */
export const USER_DELETE = {
    method: 'DELETE',
    path: '/users/:id',
    pathFor: (id) => `/users/${encodeURIComponent(id)}`,
    /**
     * Response: 200 or 204
     * Body: { message?: string } or empty
     * Errors: 400 if user is active, 404 not found, 401 unauthorized
     */
};

// ---------------------------------------------------------------------------
// PAYMENTS
// ---------------------------------------------------------------------------

/** GET /payments - Get payment record by user_id or phone_number (query params) */
export const PAYMENTS_BY_USER = {
    method: 'GET',
    path: '/payments/admin/payment',
    pathWithQuery: (params) => {
        const sp = new URLSearchParams(params);
        return `/payments/admin/payment?${sp.toString()}`;
    },
    /**
     * Request: query params – exactly one of:
     *   - user_id: string
     *   - phone_number: string
     *
     * Response: 200
     * Body: {
     *   data: {
     *     user_id: string,
     *     first_subscription_payment_captured_at: number (Unix timestamp, seconds),
     *     latest_subscription_payment_captured_at: number (Unix timestamp, seconds),
     *     payment_recurrence_count: number,
     *     subscription_end_date: number (Unix timestamp, seconds),
     *     subscription_status: string (e.g. 'ACTIVE', 'EXPIRED'),
     *     trial_payment_captured_at: number | null (Unix timestamp, seconds),
     *     trial_valid_until: number | null (Unix timestamp, seconds),
     *     updated_at: string (ISO 8601)
     *   },
     *   message?: string
     * }
     * Errors: 400 missing/invalid params, 404 not found, 401 unauthorized
     */
};

/** GET /payments/history - Get payment history list by user_id or phone_number (query params) */
export const PAYMENT_HISTORY = {
    method: 'GET',
    path: '/payments/admin/payment-history',
    pathWithQuery: (params) => {
        const sp = new URLSearchParams(params);
        return `/payments/admin/payment-history?${sp.toString()}`;
    },
    /**
     * Request: query params – exactly one of:
     *   - user_id: string
     *   - phone_number: string
     *
     * Response: 200
     * Body: {
     *   data: Array<{
     *     razorpay_payment_id: string,
     *     amount: number,
     *     created_at: string (ISO 8601),
     *     currency: string (e.g. 'INR'),
     *     razorpay_order_id: string | null,
     *     razorpay_subscription_id: string | null,
     *     status: string (e.g. 'SUCCESS'),
     *     type: string (e.g. 'TRIAL', 'SUBSCRIPTION'),
     *     user_id: string
     *   }>,
     *   message?: string
     * }
     * Errors: 400 missing/invalid params, 404 not found, 401 unauthorized
     */
};

/** POST /payments/refund - Process a refund. At least one of user_id or phone_number is required. */
export const PAYMENT_REFUND = {
    method: 'POST',
    path: '/payments/refund',
    /**
     * Request: application/json
     * Body: {
     *   user_id?: string,
     *   phone_number?: string,
     *   refund_reason: string (required)
     * }
     * At least one of user_id or phone_number is mandatory.
     *
     * Response: 200
     * Body: {
     *   data?: { refund_id?: string, status?: string },
     *   message?: string
     * }
     * Errors: 400 validation (missing user_id/phone_number, invalid payload), 404 user/payment not found, 401 unauthorized, 5xx server
     */
};
